import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import { allReviews } from '../../slice/reviewSlice'

const StarRating = ({ rating }) => {
    return (
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            {[1, 2, 3, 4, 5].map(star => (
                <i
                    key={star}
                    className={star <= rating ? 'fe-star' : 'fe-star'}
                    style={{
                        fontSize: '13px',
                        color: star <= rating ? '#ffc107' : '#dee2e6',
                        // filled vs empty using filter trick since fe-star is outline only
                        filter: star <= rating ? 'none' : 'opacity(0.4)',
                    }}
                />
            ))}
            <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: '4px' }}>
                ({rating}/5)
            </span>
        </div>
    )
}

const ReviewList = () => {
    const dispatch = useDispatch()
    const { reviewList } = useSelector(state => state.review)
    const navigate = useNavigate()

    const { page, setPage, limit, setLimit } = useModuleSettings()

    const [statusFilter, setStatusFilter] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchDebounce, setSearchDebounce] = useState('')

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        dispatch(allReviews({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined,
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    const handleView = (reviewId) => {
        navigate('/reviews/view', { state: { reviewId } })
    }

    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((reviewList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'Booking ID',
            selector: row => row.bookingR?.booking_id || 'NA',
            sortable: true,
            minWidth: '170px',
        },
        {
            name: 'Tutor',
            minWidth: '190px',
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                        src={row.tutorRating?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                        alt="tutor"
                        onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                        }}
                        style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    />
                    <div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: '#212529' }}>
                            {`${row.tutorRating?.first_name || ''} ${row.tutorRating?.last_name || ''}`.trim() || 'NA'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                            {row.tutorRating?.email || ''}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            name: 'Parent',
            minWidth: '190px',
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                        src={process.env.PUBLIC_URL + '/assets/avatar.png'}
                        alt="parent"
                        onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                        }}
                        style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    />
                    <div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: '#212529' }}>
                            {`${row.parentRating?.first_name || ''} ${row.parentRating?.last_name || ''}`.trim() || 'NA'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                            {row.parentRating?.email || ''}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            name: 'Rating',
            width: '160px',
            sortable: true,
            selector: row => row.rating,
            cell: row => <StarRating rating={row.rating || 0} />,
        },
        {
            name: 'Review',
            minWidth: '220px',
            cell: row => (
                <div
                    title={row.review}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px', fontSize: '13px' }}
                >
                    {row.review || 'NA'}
                </div>
            ),
        },
        {
            name: 'Date',
            selector: row =>
                row.createdAt ? moment(row.createdAt).local().format('DD MMM YYYY, hh:mm A') : 'NA',
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'Action',
            width: '80px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('reviews', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
    ]

    const customStyles = {
        table: { style: { backgroundColor: '#ffffff', borderRadius: '8px' } },
        headRow: { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6', minHeight: '48px' } },
        headCells: { style: { fontSize: '13px', fontWeight: '600', color: '#495057', paddingLeft: '12px', paddingRight: '12px' } },
        cells: { style: { fontSize: '13px', color: '#212529', paddingTop: '12px', paddingBottom: '12px', paddingLeft: '12px', paddingRight: '12px' } },
        rows: {
            style: { minHeight: '60px', '&:hover': { backgroundColor: '#f8f9fa', cursor: 'pointer' } },
            stripedStyle: { backgroundColor: '#ffffff' },
        },
        pagination: { style: { borderTop: '1px solid #dee2e6', minHeight: '56px', fontSize: '13px' } },
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Reviews</h4>
            </div>

            {/* Search and Filter */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="position-relative">
                                <i
                                    className="fe-search position-absolute"
                                    style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '16px' }}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by booking ID, tutor, or parent name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        paddingLeft: '38px',
                                        paddingRight: searchQuery ? '38px' : '12px',
                                        height: '40px',
                                        fontSize: '14px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-link position-absolute p-0"
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                                    >
                                        <i className="fe-x" style={{ fontSize: '16px' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                                style={{ height: '40px', fontSize: '14px', border: '1px solid #dee2e6', borderRadius: '6px' }}
                            >
                                <option value="all">All Ratings</option>
                                <option value="5">5 Stars</option>
                                <option value="4">4 Stars</option>
                                <option value="3">3 Stars</option>
                                <option value="2">2 Stars</option>
                                <option value="1">1 Star</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={reviewList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={reviewList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={reviewList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => { setLimit(newLimit); setPage(1) }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={reviewList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-inbox" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No reviews found matching your search' : 'No reviews found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Reviews will appear here'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}
                />
            </div>
        </div>
    )
}

export default ReviewList